import { noop } from 'lodash-es';
import { LanguageType } from '../../../types/language';
import { iOS } from '../env';
import { kkBridgeCall, kkBridgeCallAsync, kkBridgeHasMethod } from '../jsBridge';
import { isDev } from '../util';
import { CreFitAppVersion, NativeApi, Platform } from './interface';
import { getDevIsUIv2, getDevLang, getDevToken } from './utils';

// 重登的时候，不关闭当前页面的路由列表
const reloginShouldNotCloseWebviewRouteList = [
  '/vhk/clCredit',
  '/vhk/clCredit/',
  '/vhk/clCredit/intro',
];

export const crefitNative: NativeApi = {
  name: Platform.Crefit,
  token: '',
  deviceId: isDev() ? '1234567890' : '',
  hardwareId: isDev() ? '7E5B0714-3071-4CAF-A80B-8A9F2814F218' : '',
  isNewVersion: undefined,
  uiVersion: '',
  layoutVersion: '',
  async init(options = {}) {
    const { initLangCallBack = noop } = options;
    const curLang = await this?.getLanguage?.();
    initLangCallBack(curLang);
    this.getIsNewVersion?.();
    this.getUIVersion?.();
  },
  setToken(token: string) {
    this.token = token;
  },
  getToken() {
    if (!this.token) {
      if (isDev()) {
        return getDevToken();
      }
      return new Promise((resolve) => {
        kkBridgeCall('kk_getTokenValue', {}, (data) => {
          this.token = data;
          resolve(data);
        });
      });
    }
    return this.token;
  },
  getRequestHeader() {
    return {
      uiVersion: this.getUIVersion?.() || 'O',
    };
  },
  getDeviceId() {
    let retResolve = noop;
    const retPromise = new Promise<string>((resolve) => {
      retResolve = resolve;
    });
    if (this.deviceId) retResolve(this.deviceId);
    if (kkBridgeHasMethod('kkGetDeviceID')) {
      kkBridgeCall('kkGetDeviceID', {}, (data) => {
        retResolve(data);
      });
    } else {
      retResolve('');
    }
    return retPromise;
  },
  getHardwareId() {
    let retResolve = noop;
    const retPromise = new Promise<string>((resolve) => {
      retResolve = resolve;
    });
    if (this.hardwareId) retResolve(this.hardwareId);
    if (kkBridgeHasMethod('kkGetHardwareID')) {
      kkBridgeCall('kkGetHardwareID', {}, (data) => {
        retResolve(data);
      });
    } else {
      retResolve('');
    }
    return retPromise;
  },
  getLanguage: () => {
    let retResolve = noop;
    const retPromise = new Promise<LanguageType>((resolve) => {
      retResolve = resolve;
    });
    if (kkBridgeHasMethod('kk_getNativeLanguage')) {
      kkBridgeCall('kk_getNativeLanguage', {}, (data) => {
        const reg = /zh/;
        let language = '';
        if (data.match(reg)) {
          language = 'zh';
        } else {
          language = 'en';
        }
        retResolve(language);
      });
    } else {
      retResolve(getDevLang());
    }
    return retPromise;
  },
  closeWindow: () => {
    if (kkBridgeHasMethod('kk_closeWindow')) {
      kkBridgeCall('kk_closeWindow', {}, () => {});
    }
  },
  gotoWhatsApp: () => {
    if (kkBridgeHasMethod('kk_gotoWhatsApp')) {
      kkBridgeCall('kk_gotoWhatsApp', {});
    }
  },
  selectContact: () => {
    let retResolve = noop;
    let retReject = noop;
    const retPromise = new Promise<unknown>((resolve, reject) => {
      retResolve = resolve;
      retReject = reject;
    });
    if (kkBridgeHasMethod('kk_selectContact')) {
      kkBridgeCall('kk_selectContact', {}, (tmpData: unknown) => {
        retResolve(tmpData);
      });
    } else {
      retReject();
    }
    return retPromise;
  },
  reLogin(options?: { noCloseWindow?: boolean }) {
    let retResolve: (value: string) => void = noop;
    let retReject = noop;
    const retPromise = new Promise<string>((resolve, reject) => {
      retResolve = resolve;
      retReject = reject;
    });
    if (kkBridgeHasMethod('kk_gotoLoginModule')) {
      const currentPath = window.location.pathname;
      if (!reloginShouldNotCloseWebviewRouteList.includes(currentPath) && !options?.noCloseWindow) {
        this.closeWindow();
      }
      kkBridgeCall(
        'kk_gotoLoginModule',
        {
          entranceCode: localStorage.getItem('entranceCode') || '',
        },
        (data) => {
          if (data) {
            retResolve(String(data));
          } else {
            retReject();
          }
        }
      );
    } else {
      retReject();
    }
    return retPromise;
  },
  getStatusBarHeight() {
    let retResolve = noop;
    let retReject = noop;
    const retPromise = new Promise<number>((resolve, reject) => {
      retResolve = resolve;
      retReject = reject;
    });
    if (kkBridgeHasMethod('kk_statusBarHeight')) {
      try {
        kkBridgeCall('kk_statusBarHeight', {}, (barHeight) => {
          retResolve(barHeight);
        });
      } catch (error) {
        console.log('🚀 ~ file: index.tsx:105 ~ useEffect ~ error:', error);
        retReject();
      }
    } else {
      retReject();
    }
    return retPromise;
  },
  getNativeAppVersion() {
    if (!iOS()) {
      return kkBridgeCallAsync('kk_getAppVersion');
    }
    if (kkBridgeHasMethod('kk_getAppVersion')) {
      return generatePromise<CreFitAppVersion>((resolve) => {
        kkBridgeCall('kk_getAppVersion', {}, (data) => {
          resolve(data);
        });
      });
    }
  },
  gotoMakeCall(phone?: string) {
    if (kkBridgeHasMethod('kk_gotoMakeCall')) {
      kkBridgeCall('kk_gotoMakeCall', { phone });
    }
  },
  fbTrackEvent(eventName: string, eventParam?: Record<string, unknown>) {
    if (kkBridgeHasMethod('kk_FBTrackEvent')) {
      kkBridgeCall('kk_FBTrackEvent', { eventName, eventParam });
    }
  },
  afTrackEvent(eventName: string, eventParam?: Record<string, unknown>) {
    if (kkBridgeHasMethod('kk_AFTrackEvent')) {
      kkBridgeCall('kk_AFTrackEvent', { eventName, eventParam });
    }
  },
  gotoRate(Source_Type: 'HomePage_CV' | 'Withdrawal_success' | '7d_interest_tree_settle') {
    if (kkBridgeHasMethod('kk_gotoRateEvent')) {
      kkBridgeCall('kk_gotoRateEvent', { scene: Source_Type }, () => {});
    }
  },
  getIsNewVersion() {
    if (typeof this.isNewVersion !== 'boolean') {
      // 暂时还没保存下来，读取数据
      if (isDev()) {
        // 开发场景直接读代码配置
        this.isNewVersion = getDevIsUIv2();
        return this.isNewVersion;
      }
      if (import.meta.env.VITE_APP_NEW_VERSION === 'true' && kkBridgeHasMethod('kkGetIsUIv2')) {
        // 代码启用，并且有原生方法
        return generatePromise<boolean>((resolve) => {
          kkBridgeCall('kkGetIsUIv2', {}, (data) => {
            console.log('🚀 ~ file: kkGetIsUIv2 ~ data:', data);
            this.isNewVersion = !!Number(data);
            resolve(this.isNewVersion);
          });
        });
      }
      // 可能出现还在加载的阶段，不能给isNewVersion赋值，所以直接返回false
      return false;
    }
    return this.isNewVersion;
  },
  getNativeLayoutVersion() {
    if (!this.layoutVersion && kkBridgeHasMethod('kkGetLayoutVersion')) {
      this.layoutVersion = kkBridgeCallAsync('kkGetLayoutVersion') || '';
    }
    return this.layoutVersion || '';
  },
  getUIVersion() {
    if (!this.uiVersion && kkBridgeHasMethod('kkGetUIVersion')) {
      this.uiVersion = kkBridgeCallAsync('kkGetUIVersion') || '';
    }
    return this.uiVersion || '';
  },
  goCmcFaceVerify() {
    let retResolve = noop;
    let retReject = noop;
    const retPromise = new Promise<boolean>((resolve, reject) => {
      retResolve = resolve;
      retReject = reject;
    });

    if (kkBridgeHasMethod('kkCmcFaceVerify')) {
      kkBridgeCall('kkCmcFaceVerify', {}, (data: unknown) => {
        if (Number(data)) {
          retResolve(true);
        } else {
          retResolve(false);
        }
      });
    } else {
      console.log('🚀 ~ 没有原生方法，直接reject');
      // 没有原生方法，直接reject
      retReject();
    }
    return retPromise;
  },
  kkOpenNewWebview(url: string) {
    if (kkBridgeHasMethod('kkOpenNewWebview')) {
      const urlObj = new URL(url);
      urlObj.searchParams.set('isNewWebview', 'Y');
      kkBridgeCall('kkOpenNewWebview', { url: urlObj.toString() }, () => {});
      return true;
    }
    return false;
  },
  kkStartActivityNode(objMsg) {
    if (kkBridgeHasMethod('kkStartActivityNode')) {
      kkBridgeCall('kkStartActivityNode', objMsg, () => {});
      return true;
    }
  },
};

const generatePromise = <T, S = never>(
  callback: (
    resolve: (value: T | PromiseLike<T> | undefined | null) => void,
    reject?: ((reason: unknown) => S | PromiseLike<S>) | undefined | null
  ) => void
) => {
  let retResolve = noop;
  let retReject = noop;
  const retPromise = new Promise<T>((resolve, reject) => {
    retResolve = resolve;
    retReject = reject;
  });
  try {
    callback(retResolve, retReject);
  } catch (error) {
    retReject(error);
  }
  return retPromise;
};
